/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from '../lib/firebase';
import { createDefaultAgents } from '../lib/services/default-agents';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (data: { displayName?: string; photoURL?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to initialize welcome bonus for new users
const initializeNewUserBonus = async (userId: string) => {
  try {
    const balanceRef = doc(db, 'users', userId, 'balance', 'current');
    const balanceDoc = await getDoc(balanceRef);
    
    // Only give bonus if balance doesn't exist (new user)
    if (!balanceDoc.exists()) {
      const bonusAmount = 100;
      
      // Create balance with bonus
      await setDoc(balanceRef, {
        amount: bonusAmount,
        status: 'Active',
        lastUpdated: Timestamp.now(),
        createdAt: Timestamp.now()
      });
      
      // Create billing transaction record for bonus
      await addDoc(collection(db, 'users', userId, 'billing'), {
        type: 'credit',
        amount: bonusAmount,
        description: 'Welcome Bonus - New User Signup',
        status: 'paid',
        date: Timestamp.now(),
        source: 'signup-bonus',
        createdAt: Timestamp.now()
      });
      
      console.log(`🎉 Welcome bonus of ₹${bonusAmount} credited to new user ${userId}`);
    }
  } catch (error) {
    console.error('Error initializing new user bonus:', error);
    // Don't block auth flow if bonus fails
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Set user and loading state immediately to prevent hanging
      setUser(user);
      setLoading(false);
      
      // Run async operations in background without blocking UI
      if (user) {
        // Use setTimeout to ensure these operations don't block the loading state
        setTimeout(async () => {
          try {
            // Create user document if it doesn't exist
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (!userDoc.exists()) {
              await setDoc(userDocRef, {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || '',
                photoURL: user.photoURL || '',
                createdAt: new Date().toISOString(),
                lastLoginAt: new Date().toISOString(),
                preferences: {
                  theme: 'light',
                  language: 'en',
                  notifications: true
                }
              });
              
              // Create default agents for new users (non-blocking)
              createDefaultAgents(user.uid).catch(error => {
                console.error('Error creating default agents:', error);
              });
              
              // Give welcome bonus to new user (non-blocking)
              initializeNewUserBonus(user.uid).catch(error => {
                console.error('Error giving welcome bonus:', error);
              });
            } else {
              // Update last login time (non-blocking)
              setDoc(userDocRef, {
                lastLoginAt: new Date().toISOString()
              }, { merge: true }).catch(error => {
                console.error('Error updating last login time:', error);
              });
            }
          } catch (error) {
            console.error('Error in auth state change:', error);
            // Don't block auth flow if Firestore operations fail
          }
        }, 0);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Secret test credentials - auto-create user if doesn't exist
      const SECRET_EMAIL = '12@a.com';
      const SECRET_PASSWORD = '123';
      // Firebase requires at least 6 characters, so we use a longer password internally
      const SECRET_PASSWORD_INTERNAL = '123456';
      
      if (email === SECRET_EMAIL && password === SECRET_PASSWORD) {
        try {
          // Try to sign in first with the internal password
          await signInWithEmailAndPassword(auth, email, SECRET_PASSWORD_INTERNAL);
        } catch (signInError: any) {
          // If user doesn't exist, create the account automatically
          if (signInError.code === 'auth/user-not-found' || signInError.code === 'auth/invalid-credential' || signInError.code === 'auth/wrong-password') {
            try {
              // Create the secret test user with a valid password length
              const { user } = await createUserWithEmailAndPassword(auth, email, SECRET_PASSWORD_INTERNAL);
              await updateProfile(user, { displayName: 'Test User' });
              
              // Create user document
              const userDocRef = doc(db, 'users', user.uid);
              await setDoc(userDocRef, {
                uid: user.uid,
                email: user.email,
                displayName: 'Test User',
                photoURL: '',
                createdAt: new Date().toISOString(),
                lastLoginAt: new Date().toISOString(),
                preferences: {
                  theme: 'light',
                  language: 'en',
                  notifications: true
                }
              });
              
              // Create default agents for new user (non-blocking)
              createDefaultAgents(user.uid).catch(error => {
                console.error('Error creating default agents:', error);
              });
              
              // Give welcome bonus (non-blocking)
              initializeNewUserBonus(user.uid).catch(error => {
                console.error('Error giving welcome bonus:', error);
              });
              
              // Sign in successful after creation
              return;
            } catch (createError: any) {
              console.error('Error creating test account:', createError);
              // Provide more specific error message
              if (createError.code === 'auth/email-already-in-use') {
                // User exists but password might be wrong, try signing in again
                try {
                  await signInWithEmailAndPassword(auth, email, SECRET_PASSWORD_INTERNAL);
                  return;
                } catch (retryError: any) {
                  throw new Error('Account exists but password is incorrect. Please contact support.');
                }
              } else if (createError.code === 'auth/weak-password') {
                throw new Error('Password is too weak. Please use a stronger password.');
              } else {
                throw new Error(`Failed to create account: ${createError.message || 'Unknown error'}`);
              }
            }
          } else {
            throw signInError;
          }
        }
        return;
      }
      
      // Normal sign in flow for other users
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      // Provide user-friendly error messages
      let errorMessage = 'An error occurred during sign in.';
      
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Please try again later.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message.replace('Firebase: ', '').replace('Error (', '').replace(')', '');
      }
      
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(user, { displayName });
      
      // Create user document
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName,
        photoURL: '',
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        preferences: {
          theme: 'light',
          language: 'en',
          notifications: true
        }
      });
      
      // Create default agents for new users
      try {
        await createDefaultAgents(user.uid);
      } catch (error) {
        console.error('Error creating default agents:', error);
        // Don't block auth flow if default agents fail
      }
      
      // Give welcome bonus to new user
      try {
        await initializeNewUserBonus(user.uid);
      } catch (error) {
        console.error('Error giving welcome bonus:', error);
        // Don't block auth flow if bonus fails
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      // Handle popup blocked error with better message
      if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again, or use email/password sign in.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        throw new Error('Sign in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign in popup was closed. Please try again.');
      }
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const updateUserProfile = async (data: { displayName?: string; photoURL?: string }) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      await updateProfile(user, data);
      
      // Update user document
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, data, { merge: true });
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
